@let banner = bannerStore.banner;
@let hasChanges = hasUnsavedChanges(bannerForm);
@if (bannerStore.isLoading()) {
  <div class="pt-32">
    <aw-loading-animation></aw-loading-animation>
  </div>
}
@if (banner() && bannerForm && !bannerStore.isLoading()) {
  <!-- LanguageSwitcher -->
  <div [formGroup]="languageForm" class="flex w-full justify-end">
    <aw-language-switcher
      formControlName="language"
      [label]="cmsTextProvider.shared.languageSelection"
      id="language-select">
    </aw-language-switcher>
  </div>

  <aw-section [formGroup]="bannerForm" testIdAffix="banner">
    <!-- BannerHeader -->
    <aw-section-header>
      <aw-section-headline>{{
        cmsTextProvider.shared.banner
      }}</aw-section-headline>
      <aw-section-header-actions>
        <aw-reveal-form-errors-button
          [form]="bannerForm"></aw-reveal-form-errors-button>
        <aw-button
          [id]="'banner-save'"
          [style]="'primary'"
          (click)="save()"
          [isDisabled]="!bannerForm.valid || !hasChanges"
          [type]="'submit'"
          [text]="cmsTextProvider.shared.save">
        </aw-button>
        <aw-button
          [id]="'banner-cancel'"
          [style]="'primary-alt'"
          [type]="'reset'"
          [isDisabled]="!hasChanges"
          (click)="handleAbortChangesDialog()"
          [text]="cmsTextProvider.shared.cancel">
        </aw-button>
      </aw-section-header-actions>
    </aw-section-header>

    <!-- Accessibility Hint-->
    <aw-accessibility-banner>
      <p>
        {{ cmsTextProvider.shared.accessibilityHeadline }}
      </p>
      <ul class="list-disc pl-5">
        @for (hint of cmsTextProvider.shared.accessibilityHints; track $index) {
          <li>{{ hint }}</li>
        }
      </ul>
    </aw-accessibility-banner>

    <!-- BannerImageSection -->
    <aw-section
      [isDisabled]="!bannerForm.controls.isImageActive.value"
      testIdAffix="bannerImage">
      <aw-section-header>
        <aw-section-headline>
          {{ cmsTextProvider.banner.image }}
        </aw-section-headline>
        <aw-section-header-actions>
          <aw-toggle
            [formControl]="bannerForm.controls.isImageActive"
            id="imageActive"
            [label]="cmsTextProvider.shared.setActive" />
        </aw-section-header-actions>
      </aw-section-header>
      <span>
        {{ cmsTextProvider.banner.imageInfo }}
      </span>
      <div class="flex w-full items-start gap-6">
        <aw-image-select
          [formControl]="bannerForm.controls.accessibleImage.controls.image"
          [label]="cmsTextProvider.shared.image"
          [persistUsageOnSelect]="true"
          [usageLabel]="'banner.image'"
          id="image" />
        <aw-number-input
          [formControl]="bannerForm.controls.imageHeight"
          [label]="cmsTextProvider.banner.heightInPixels"
          id="imageHeight" />
        <aw-translation-accessibility-text-input
          [entityType]="EntityType.banner"
          [attr.scroll-mark]="
            bannerForm.controls.accessibleImage.value.imageAlts?.[0]?.id
          "
          [relationType]="TextRelationType.imageAlt"
          [formControl]="bannerForm.controls.accessibleImage.controls.imageAlts"
          [label]="cmsTextProvider.banner.description"
          id="imageAlts" />
      </div>
    </aw-section>

    <!-- BannerTextSection -->
    <aw-section
      [isDisabled]="!bannerForm.controls.isTextActive.value"
      testIdAffix="slogan">
      <aw-section-header>
        <aw-section-headline>{{
          cmsTextProvider.banner.advertisementText
        }}</aw-section-headline>
        <aw-section-header-actions>
          <aw-toggle
            [formControl]="bannerForm.controls.isTextActive"
            id="textActive"
            [label]="cmsTextProvider.shared.setActive" />
        </aw-section-header-actions>
      </aw-section-header>
      <span>
        {{ cmsTextProvider.banner.advertisementInfo }}
      </span>

      <!-- Title-->
      <div class="flex flex-col gap-6">
        <aw-translation-text-input
          [entityType]="EntityType.banner"
          [attr.scroll-mark]="bannerForm.controls.headlines.value?.[0]?.id"
          [relationType]="TextRelationType.headline"
          class="w-full"
          [formControl]="bannerForm.controls.headlines"
          [label]="cmsTextProvider.shared.title"
          id="headlines" />
        <div class="flex w-full items-start gap-7">
          <aw-number-input
            [formControl]="bannerForm.controls.headlineFontSize"
            [label]="cmsTextProvider.banner.fontSize"
            id="headlinesFontSize" />
          <aw-button-group
            id="headlineFontSizeButtonGroup"
            [options]="headlineFontSizes | enumToButtonOptionArray"
            [formControl]="headlineFontSizeBtn"
            (valueChange)="onHeadlinesFontSizeChange($event)" />
        </div>
      </div>

      <!-- Subtitle-->
      <div class="flex flex-col gap-6">
        <aw-translation-text-input
          [entityType]="EntityType.banner"
          [attr.scroll-mark]="bannerForm.controls.subHeadlines.value?.[0]?.id"
          [relationType]="TextRelationType.subHeadline"
          [formControl]="bannerForm.controls.subHeadlines"
          [label]="cmsTextProvider.banner.subTitle"
          id="subHeadlines" />
        <div class="flex w-full items-start gap-7">
          <aw-number-input
            [formControl]="bannerForm.controls.subHeadlineFontSize"
            [label]="cmsTextProvider.banner.fontSize"
            id="subHeadlinesFontSize" />
          <aw-button-group
            id="subHeadlineFontSizeButtonGroup"
            [options]="subHeadlineFontSizes | enumToButtonOptionArray"
            [formControl]="subHeadlineFontSizeBtn"
            (valueChange)="onSubHeadlinesFontSizeChange($event)" />
        </div>
      </div>

      <!-- Alignment-->
      <div class="flex gap-6">
        <aw-drop-down
          id="textAlignmentHorizontal"
          [label]="cmsTextProvider.banner.horizontalAlign"
          name="label"
          valueAttribute="value"
          [formControl]="bannerForm.controls.textAlignmentHorizontal"
          [options]="horizontalAlignments | enumToButtonOptionArray">
        </aw-drop-down>
        <aw-drop-down
          id="textAlignmentVertical"
          [label]="cmsTextProvider.banner.verticalAlign"
          name="label"
          valueAttribute="value"
          [formControl]="bannerForm.controls.textAlignmentVertical"
          [options]="verticalAlignments | enumToButtonOptionArray">
        </aw-drop-down>
      </div>

      <!-- Text Color-->
      <div class="flex w-full items-start gap-7">
        <aw-color-picker-input
          [formControl]="bannerForm.controls.textColor"
          [label]="cmsTextProvider.shared.fontColour"
          id="textColor" />
        <aw-button-group
          id="textColorButtonGroup"
          [options]="fontColors | enumToButtonOptionArray"
          [formControl]="textColorBtn"
          (valueChange)="onFontColorChange($event)" />
      </div>

      <!-- Font -->
      <div class="flex w-full items-start gap-7">
        <aw-font-select
          [formControl]="bannerForm.controls.textFont"
          [label]="cmsTextProvider.shared.fontFamily"
          id="textFont" />
        <aw-button-group
          id="textFontButtonGroup"
          [options]="fontSizes | enumToButtonOptionArray"
          [formControl]="textFontBtn"
          (valueChange)="onFontChange($event)" />
      </div>
    </aw-section>
  </aw-section>
  <aw-preview [components]="[BannerComponent]" [previewData]="previewData()" />
}
